import * as React from 'react'
import * as ReactDOM from 'react-dom'

import { Observable, Subject } from 'rxjs'

import { Injectable } from '../../injector'

import * as styles from './ModalService.css'
import * as dialogStyles from '../../ui/dialog/Dialog.css'
import { getStore } from '../../redux/Store'
import { Provider } from 'react-redux'

/**
 * This factory function exists to enforce that any modal service that isn't
 * injected is created with an overlay element. Without this, someone could
 * construct a modal service that accidentally clobbers over the singleton
 * injectable modals.
 *
 * The use case for passing in a different overlay is if the modal shouldn't
 * cover the entire screen but only a part of it. We use this for tables so
 * that they don't interfere with other games in multi-table mode.
 */
export function createModalService(overlayEl: HTMLDivElement) {
	return new ModalService(overlayEl)
}

@Injectable()
export class ModalService {
	private nextModalId = 1
	private openedModalCount: number = 0
	private overlayEl: HTMLDivElement
	private modalSet: ModalDescriptor[] = []

	constructor(overlayEl?: HTMLDivElement) {
		this.overlayEl = overlayEl || document.getElementById('cashier-dialog') as HTMLDivElement
	}

	openDialog(jsx: JSX.Element | JSX.Element[], options: ModalOptions = {}): ModalController {
		return this.openOverlay(jsx, (targetJsx, id, modalClickAway, close) => (
			<div key={id}>
				<div className={dialogStyles.dialogOverlay + ' dialog-overlay ' + (options.isBackgroundTransparent && dialogStyles.dialogOverlayTransparent)}>
					<div className={styles.dialogOverlay + ' ' + (options.isBackgroundTransparent && styles.dialogOverlayTransparent)} onClick={modalClickAway}/>
					{targetJsx}
				</div>
			</div>
		), options)
	}

	openModal(jsx: JSX.Element | JSX.Element[], options: ModalOptions = {}): ModalController {
		return this.openOverlay(jsx, (targetJsx, id, modalClickAway, close) => {
			return (
				<div key={id}>
					<div className={styles.overlay} onClick={modalClickAway}/>
					<div className={styles.modalWrapper}>
						<div className={styles.modalHeader}>
							<span>{options.title || ''}</span>
							<span className={styles.modalClose + ' fa fa-times'} onClick={close}/>
						</div>
						{targetJsx}
					</div>
				</div>
			)
		}, options)
	}

	openCustomModal(jsx: JSX.Element | JSX.Element[], options: ModalOptions = {}): ModalController {
		return this.openOverlay(jsx, (targetJsx, id) => {
			return (
				<div key={id}>
					{targetJsx}
				</div>
			)
		}, options)
	}

	closeAllModals() {
		this.modalSet.forEach(modalDescriptor => {
			modalDescriptor.controller.close()
		})
	}

	getOpenedModalCount(): number {
		return this.openedModalCount
	}

	private openOverlay(
		jsx: JSX.Element | JSX.Element[], 
		wrapFn: (targetJsx: JSX.Element | JSX.Element[], id: number, modalClickAway: () => void, close: () => void) => JSX.Element, 
		options: ModalOptions = {}
	): ModalController {
		const id = this.nextModalId++
		const modalClose$ = new Subject<void>()

		const reRender = (newJsx: JSX.Element | JSX.Element[]): void => {
			if (!modalController.isOpen) return

			const newDescriptor = {
				...modalDescriptor,
				jsx: wrapFn(newJsx, id, modalClickAway, close),
			}
			this.modalSet = [...this.modalSet]
			this.modalSet.splice(this.modalSet.indexOf(modalDescriptor), 1, newDescriptor)

			this.renderModals()
		}

		const close = (): void => {
			if (!modalController.isOpen) return
			modalController.isOpen = false

			this.modalSet = [...this.modalSet]
			this.modalSet.splice(this.modalSet.indexOf(modalDescriptor), 1)

			modalClose$.next()
			modalClose$.complete()

			this.renderModals()
		}

		const modalClickAway = (): void => {
			if (modalDescriptor.closeOnClickAway) {
				modalController.close()
			}
		}

		const modalController: ModalController = {
			isOpen: true,
			reRender,
			close,
			close$: modalClose$.asObservable(),
		}

		modalController.close$.subscribe( () => {
			this.openedModalCount--
		})

		const modalDescriptor: ModalDescriptor = {
			controller: modalController,
			jsx: wrapFn(jsx, id, modalClickAway, close),
			...options,
		}

		this.modalSet = [...this.modalSet, modalDescriptor]
		this.renderModals()
		this.openedModalCount++

		return modalController
	}

	private renderModals(): void {
		const modals = (
			<Provider store={getStore()}>
			<div>
				{this.modalSet.map(md => md.jsx)}
			</div>
			</Provider>
		)
		ReactDOM.render(modals, this.overlayEl)
	}
}

export interface ModalController {
    isOpen: boolean;

    close$: Observable<void>;

    reRender(jsx: JSX.Element | JSX.Element[]): void;

    close(): void;
}


export interface ModalOptions {
    title?: string;
    closeOnClickAway?: boolean;
	isBackgroundTransparent?: boolean;
}

interface ModalDescriptor extends ModalOptions {
    controller: ModalController;
    jsx: JSX.Element;
}
